import React from 'react';
import { FOOTER_SECTION_ID, HEADER_SECTION_ID } from 'builder/util/constants';
import { isDescendantOfSection } from 'shared/state/misc/footerNormalization';

/**
 * Shifts descendants without mutating the Redux-backed page or shared widget
 * references. This keeps a default footer container drag local to its page.
 *
 * @param page page containing the descendants
 * @param descendantIds widget ids to shift
 * @param xDifference horizontal drag delta
 * @param yDifference vertical drag delta
 * @returns a page copy with shifted descendant coordinates
 */
export const shiftDescendantWidgetLocations = (
  page: PageVersion,
  descendantIds: string[],
  xDifference: number,
  yDifference: number
): PageVersion => {
  const content = { ...page.content };

  descendantIds.forEach(descendantId => {
    const descendant = content[descendantId];
    if (!descendant) return;

    content[descendantId] = {
      ...descendant,
      config: {
        ...descendant.config,
        xLocation: (descendant.config?.xLocation ?? 0) + xDifference,
        yLocation: (descendant.config?.yLocation ?? 0) + yDifference,
      },
    };
  });

  return {
    ...page,
    content,
  };
};

/**
 * Determines whether a drag is a shared header/footer content edit. Section
 * roots are excluded because their placement belongs to the current page.
 *
 * @param content page widget content
 * @param widgetId dragged widget id
 * @returns true when the dragged default widget is inside a default section
 */
export const shouldPropagateDefaultWidgetMove = (
  content: PageContent,
  widgetId: string
): boolean => {
  if (
    widgetId === HEADER_SECTION_ID ||
    widgetId === FOOTER_SECTION_ID ||
    !content[widgetId]?.defaultWidget
  ) {
    return false;
  }

  return (
    isDescendantOfSection(content, widgetId, HEADER_SECTION_ID) ||
    isDescendantOfSection(content, widgetId, FOOTER_SECTION_ID)
  );
};

/**
 * injectTracking
 *
 * Clones a valid React element and injects the tracking prop if it is not already present.
 * Centralizes analytics tracking propagation so individual widget render components
 * do not all need to be decorated with tracking HOCs. This keeps changes minimal and
 * ensures every widget can emit analytics events via useTrackWidget.
 *
 * Example:
 * const cloned = injectTracking(<MyWidget /> , trackingObj);
 * // cloned now has prop tracking=trackingObj
 *
 * @param content React element or other renderable content.
 * @param tracking The tracking object (e.g., from react-tracking HOC) providing trackEvent.
 * @returns The original content if not a valid element or already has tracking; otherwise a cloned element with tracking injected.
 */
export const injectTracking = (content: any, tracking: any) => {
  if (React.isValidElement(content)) {
    if ((content.props as any)?.tracking) return content;
    return React.cloneElement(content as any, { tracking });
  }
  return content;
};
